import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Search, Loader2 } from 'lucide-react'
import Button from './ui/Button'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

interface FundSearchModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectFund: (projectId: string, fundName: string) => void
}

interface FundInfo {
  proj_id: string
  proj_abbr_name: string
  proj_name_th: string
  management_company?: string
  fund_type?: string
}

interface NavData {
  class_abbr_name: string
  last_val: number
  buy_price?: number
  sell_price?: number
  nav_date?: string
}

const FundSearchModal: React.FC<FundSearchModalProps> = ({ isOpen, onClose, onSelectFund }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<FundInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [loadingNavId, setLoadingNavId] = useState<string | null>(null)
  const [selectedFund, setSelectedFund] = useState<FundInfo | null>(null)
  const [navData, setNavData] = useState<NavData[]>([])
  const [showNavSelection, setShowNavSelection] = useState(false)

  const searchFunds = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setHasSearched(false)
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // Use backend API to search SEC Thailand funds
      const response = await apiFetch('/api/funds/search', {
        method: 'POST',
        body: JSON.stringify({ name: query })
      })

      if (response.ok) {
        console.log('response.ok')
        const data = await response.json()

        if (data && Array.isArray(data)) {
          // Limit to 50 results for performance
          const limitedResults = data.slice(0, 50)
          setSearchResults(limitedResults)
        } else {
          setSearchResults([])
          toast.error('No funds found')
        }
      } else {
        console.error('Response not ok:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Error response:', errorText)
        throw new Error(`Failed to fetch fund data: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error searching funds:', error)
      setSearchResults([])
      toast.error('Error searching funds. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    searchFunds(searchQuery)
  }

  const fetchDailyNav = async (projId: string): Promise<NavData[] | null> => {
    try {
      // Use backend API to get NAV data (it will automatically try the last 7 days)
      const response = await apiFetch(`/api/funds/${projId}/nav`)

      if (response.ok) {
        const data = await response.json()
        if (data && data.length > 0) {
          console.log(`NAV data found: ${data.length} fund classes`)
          return data
        }
      } else {
        const errorData = await response.json()
        console.error('NAV API error:', errorData)
      }
    } catch (error) {
      console.error('Error fetching NAV data:', error)
    }

    console.error('NAV data not found')
    return null
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleSelectFund = async (fund: FundInfo) => {
    setLoadingNavId(fund.proj_id)
    
    try {
      const fetchedNavData = await fetchDailyNav(fund.proj_id)
      
      if (fetchedNavData && fetchedNavData.length > 0) {
        setSelectedFund(fund)
        setNavData(fetchedNavData)
        setShowNavSelection(true)
        toast.success(`Found ${fetchedNavData.length} fund classes`)
        console.log('NAV Data:', fetchedNavData)
      } else {
        toast.error('No recent NAV data found for this fund')
      }
    } catch (error) {
      console.error('Error fetching NAV data:', error)
      toast.error('Error fetching NAV data')
    } finally {
      setLoadingNavId(null)
    }
  }

  const handleSelectNavItem = (navItem: NavData) => {
    if (selectedFund) {
      onSelectFund(selectedFund.proj_id, navItem.class_abbr_name || selectedFund.proj_name_th)
      onClose()
    }
  }

  const handleBackToSearch = () => {
    setShowNavSelection(false)
    setSelectedFund(null)
    setNavData([])
  }

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('')
      setSearchResults([])
      setHasSearched(false)
      setLoading(false)
      setShowNavSelection(false)
      setSelectedFund(null)
      setNavData([])
      setLoadingNavId(null)
    }
  }, [isOpen])

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="sheet fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {showNavSelection && (
                  <button
                    onClick={handleBackToSearch}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Back to search"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  {showNavSelection ? `Select Fund Class - ${selectedFund?.proj_name_th}` : 'Search Thai Funds'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Input - Only show when not in NAV selection mode */}
            {!showNavSelection && (
              <div className="mb-6">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Search by fund name, project ID, or management company..."
                      className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={loading || !searchQuery.trim()}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Search Results or NAV Selection */}
            <div className="flex-1 overflow-hidden">
              {/* NAV Selection Interface */}
              {showNavSelection && navData.length > 0 && (
                <div className="overflow-y-auto max-h-full">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Select a fund class to continue:
                    </p>
                  </div>
                  <div className="space-y-1">
                    {navData.map((navItem, index) => (
                      <motion.div
                        key={`${navItem.class_abbr_name}-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSelectNavItem(navItem)}
                        className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              {navItem.class_abbr_name}
                            </h3>
                            <div className="mt-0.5 space-y-0.5">
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                Fund Class: {navItem.class_abbr_name}
                              </p>
                              <div className="flex gap-3">
                                {navItem.last_val && (
                                  <p className="text-xs text-primary-600 dark:text-primary-400">
                                    NAV: {navItem.last_val.toLocaleString()}
                                  </p>
                                )}
                                {navItem.buy_price && (
                                  <p className="text-xs text-gain">
                                    Buy: {navItem.buy_price.toLocaleString()}
                                  </p>
                                )}
                                {navItem.sell_price && (
                                  <p className="text-xs text-orange-600 dark:text-orange-400">
                                    Sell: {navItem.sell_price.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results - Only show when not in NAV selection mode */}
              {!showNavSelection && (
                <>
                  {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Searching funds...</span>
                </div>
              )}

              {!loading && hasSearched && searchResults.length === 0 && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">No funds found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Try searching with different keywords
                    </p>
                  </div>
                </div>
              )}

              {!loading && !hasSearched && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">Enter search terms to find funds</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Search by fund name, project ID, or management company
                    </p>
                  </div>
                </div>
              )}

              {!loading && searchResults.length > 0 && (
                <div className="overflow-y-auto max-h-full">
                  <div className="space-y-1">
                    {searchResults.map((fund, index) => (
                      <motion.div
                        key={fund.proj_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSelectFund(fund)}
                        className={`p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
                          loadingNavId === fund.proj_id ? 'opacity-50 pointer-events-none' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {fund.proj_name_th}
                            </h3>
                            <div className="mt-0.5 space-y-0.5">
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                {fund.proj_abbr_name}
                              </p>
                              <p className="text-xs text-primary-600 dark:text-primary-400 font-mono">
                                ID: {fund.proj_id}
                              </p>
                              {fund.management_company && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                  {fund.management_company}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            {loadingNavId === fund.proj_id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                            ) : (
                              <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Data from SEC Thailand API
                </p>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default FundSearchModal